import { useState } from "react";
import { Card, CardHead, Skeleton, rupees } from "./parts";

const LABELS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
const NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CHART_HEIGHT = 180;

// Month-by-month bars for the selected year, with the same month last year
// as a tick. The current month is solid; the year's biggest month is amber.
function MonthChart({ year, months }) {
  const [hover, setHover] = useState(null);
  const { current, previous, loading } = months;
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth();
  // Scaled to the selected year so its bars stay readable; last year's marks
  // above that are capped at the top of the chart.
  const max = Math.max(1, ...current);
  const peak = current.length ? current.indexOf(Math.max(...current)) : -1;
  const hasPrevious = previous.some((value) => value > 0);
  const height = (value) => `${Math.round((Math.min(value, max) / max) * CHART_HEIGHT)}px`;

  return (
    <Card $pad="16px 18px 14px">
      <CardHead>
        <span>Month by month</span>
        <span style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 11.5, color: "var(--ink2)" }}>
          <Legend swatch={{ width: 10, height: 10, borderRadius: 2, background: "var(--acc)" }}>{year}</Legend>
          {hasPrevious && (
            <Legend swatch={{ width: 12, borderTop: "2px solid var(--ink3)" }}>{year - 1} same month</Legend>
          )}
          <Legend swatch={{ width: 10, height: 10, borderRadius: 2, background: "var(--warn)" }}>Biggest month</Legend>
        </span>
      </CardHead>

      {loading ? (
        <Skeleton $h={CHART_HEIGHT + 20} />
      ) : (
        <div
          style={{
            position: "relative",
            height: CHART_HEIGHT + 24,
            display: "flex",
            alignItems: "flex-end",
            gap: 8,
          }}
        >
          {LABELS.map((label, i) => {
            const value = current[i];
            const known = value !== undefined;
            const last = previous[i] || 0;
            const isNow = year === thisYear && i === thisMonth;
            const color =
              i === peak && value > 0 ? "var(--warn)" : isNow ? "var(--acc)" : "var(--acc-mid)";
            return (
              <div
                key={i}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                style={{
                  flex: 1,
                  height: "100%",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  paddingBottom: 20,
                }}
              >
                {known && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: `calc(${height(value)} + 28px)`,
                      padding: "4px 7px",
                      borderRadius: 6,
                      background: "var(--ink)",
                      color: "var(--surface)",
                      fontSize: 10.5,
                      whiteSpace: "nowrap",
                      opacity: hover === i ? 1 : 0,
                      transform: `translateY(${hover === i ? 0 : 4}px)`,
                      transition: "opacity .15s, transform .2s",
                      pointerEvents: "none",
                      zIndex: 2,
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {NAMES[i]} · {rupees(value)}
                    </div>
                    {hasPrevious && (
                      <div style={{ opacity: 0.75 }}>
                        {year - 1}: {rupees(last)}
                      </div>
                    )}
                  </div>
                )}
                <div
                  style={{
                    width: "100%",
                    maxWidth: 44,
                    height: known ? `max(${height(value)}, 3px)` : 3,
                    borderRadius: "5px 5px 2px 2px",
                    background: known ? color : "var(--surface2)",
                    filter: hover === i ? "brightness(1.08)" : "none",
                    transition: "height .3s, filter .15s",
                  }}
                />
                {hasPrevious && last > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: `calc(${height(last)} + 20px)`,
                      width: "64%",
                      maxWidth: 30,
                      borderTop: "2px solid var(--ink3)",
                      opacity: 0.8,
                    }}
                  />
                )}
                <span
                  style={{
                    position: "absolute",
                    bottom: 0,
                    fontSize: 10.5,
                    color: isNow ? "var(--ink)" : "var(--ink3)",
                    fontWeight: isNow ? 600 : 400,
                  }}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

const Legend = ({ swatch, children }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
    <span style={swatch} />
    {children}
  </span>
);

export default MonthChart;
